// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AccountLogo } from '@advisor-app/components/account-logo';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { CopilotLinkRenderer } from './copilot-link-renderer';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { useCallback, useRef } from 'react';
import { convert } from 'html-to-text';
import { Tooltip } from '@mui/material';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { useTranslation } from 'react-i18next';

type Sender = 'CITEC' | 'HUMAN';

interface CopilotChatMessageProps {
  message: string;
  sender: Sender;
  loading?: boolean;
  allowCopy?: boolean;
}

export const CopilotChatMessage = ({
  message,
  sender,
  loading,
  allowCopy,
}: CopilotChatMessageProps) => {
  const { t } = useTranslation();
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const onClick = useCallback(() => {
    const container = chatContainerRef.current;
    /* istanbul ignore if */
    if (container !== null) {
      const markdown = container
        .querySelector('.markdown')
        ?.cloneNode(true) as HTMLElement;

      markdown.removeAttribute('class');

      const data = new ClipboardItem({
        'text/html': new Blob([markdown.outerHTML], {
          type: 'text/html',
        }),
        'text/plain': new Blob(
          [
            convert(markdown.outerHTML, {
              selectors: [{ selector: 'table', format: 'dataTable' }],
            }) ?? '',
          ],
          {
            type: 'text/plain',
          },
        ),
      });

      navigator.clipboard.write([data]);
    }
  }, []);

  return (
    <div className="my-4 flex animate-fade px-4">
      {sender === 'CITEC' && (
        <div className="mr-2 h-[30px] w-[30px] rounded-[50%] bg-copilot-blue">
          <AppIcon
            icon="CopilotChatButton"
            className="text-copilot-icon"
            size={30}
          />
        </div>
      )}

      <div
        className={classNames('chat-bubble aria-busy:!max-w-16', {
          'ml-auto': sender === 'HUMAN',
        })}
        aria-busy={loading}
        data-testid="chat-message-bubble"
      >
        {sender === 'CITEC' ? (
          <div
            className="absolute left-[-10px] top-0"
            data-testid="chat-caret-left"
          >
            <AppIcon icon="ChatCaretLeft" size="base" />
          </div>
        ) : (
          <div
            className="absolute right-[-10px] top-0"
            data-testid="chat-caret-right"
          >
            <AppIcon icon="ChatCaretRight" size="base" />
          </div>
        )}
        <div
          className="chat-content group group flex items-start gap-x-2"
          aria-busy={loading}
          ref={chatContainerRef}
          data-testid="chat-message"
        >
          <Markdown
            components={{ a: CopilotLinkRenderer }}
            className="markdown prose w-full max-w-[calc(100%-30px)] select-text text-copilot-text group-aria-busy:hidden"
            remarkPlugins={[remarkGfm]}
          >
            {message}
          </Markdown>
          <div
            className="hidden items-center gap-x-1 group-aria-busy:flex"
            data-testid="chat-loading"
          >
            {Array.from({ length: 3 }, (_, key) => (
              <span
                key={key}
                className="h-2 w-2 animate-pulse rounded-full bg-primary-medium"
              />
            ))}
          </div>
          {sender === 'CITEC' && !loading && allowCopy && (
            <Tooltip
              title={t('copilot.copy-to-clipboard', { ns: 'components' })}
              placement="top"
            >
              <span>
                <IconButton
                  icon="Copy"
                  variant="icon"
                  hoverable
                  onClick={onClick}
                  data-testid="chat-copy"
                  className="!hidden group-hover:!inline-flex"
                />
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      {sender === 'HUMAN' && (
        <div className="ml-3 mr-3 h-9 w-9">
          <AccountLogo accountUser />
        </div>
      )}
    </div>
  );
};
